import { FilterCompileError } from '@/utils/filter-compile-error.util';
import type { StartupProgramFilter } from './startup-programs.types';

export const PROGRAM_UNAVAILABLE =
  'Startup program filters are unavailable for this organization. Remove the program filter or try again when access is enabled.';
export const RELATIONSHIP_LABELS = {
  participation: 'Program participation',
  investment: 'Investment portfolio',
  affiliation: 'Other affiliation',
} as const;

// Malformed saved restrictions remain active so validation blocks the request.
export function hasStartupPrograms(value: unknown): boolean {
  if (value == null) return false;
  const selections = (value as StartupProgramFilter).selections;
  return !Array.isArray(selections) || selections.length > 0;
}

export function startupProgramRequest(
  value: unknown
): StartupProgramFilter | undefined {
  if (!hasStartupPrograms(value)) return undefined;
  const filter = value as StartupProgramFilter;
  if (
    !['any', 'all'].includes(filter.match) ||
    !Array.isArray(filter.selections) ||
    filter.selections.some(
      (s) =>
        !s ||
        typeof s.program_id !== 'string' ||
        !s.program_id.trim() ||
        !Object.prototype.hasOwnProperty.call(
          RELATIONSHIP_LABELS,
          s.relationship_type
        ) ||
        !Array.isArray(s.cohort_ids) ||
        s.cohort_ids.some((id) => typeof id !== 'string' || !id.trim())
    )
  ) {
    throw new FilterCompileError(
      'startupPrograms',
      'Startup programs',
      'This saved startup program filter is invalid. Remove it and select the programs again.'
    );
  }
  // Snapshot stable identifiers; never derive a broad company-domain query.
  return {
    match: filter.match,
    selections: filter.selections.map((s) => ({
      ...s,
      cohort_ids: [...s.cohort_ids],
    })),
  };
}

export function isStartupProgramSortingError(message: string): boolean {
  return /^Sorting across more than [\d,]+ (?:companies|employers) is not available yet\./.test(
    message
  );
}

export class ProgramSearchError extends Error {}

export function startupProgramErrorMessage(
  error: unknown,
  fallback: string
): string {
  if (
    error instanceof ProgramSearchError ||
    error instanceof FilterCompileError
  )
    return error.message;
  const detail = (error as { data?: { detail?: unknown } } | null)?.data
    ?.detail;
  return typeof detail === 'string' ? detail : fallback;
}

/** Preserve the caller's raw-row scan budget, even when local per-company caps discard rows. */
export const MAX_PROGRAM_IMPORT_PAGES = 100;
export const MAX_EMPTY_PROGRAM_IMPORT_PAGES = 10;
export async function collectProgramPages<T>(
  limit: number,
  fetchPage: (
    cursor: string | undefined,
    remaining: number
  ) => Promise<{ rows: T[]; next_cursor?: string | null }>,
  selectRows: (rows: T[]) => T[] = (rows) => rows
): Promise<T[]> {
  if (!Number.isInteger(limit) || limit < 1)
    throw new ProgramSearchError(
      'Choose a positive whole-number import limit.'
    );
  let rows: T[] = [];
  let rawFetched = 0;
  let pages = 0;
  let emptyPages = 0;
  const seen = new Set<string>();
  let cursor: string | undefined;
  do {
    const remaining = limit - rawFetched;
    const page = await fetchPage(cursor, remaining);
    if (page.rows.length > remaining)
      throw new ProgramSearchError(
        'Search returned more rows than requested. Retry with a smaller import.'
      );
    rawFetched += page.rows.length;
    pages++;
    emptyPages = page.rows.length ? 0 : emptyPages + 1;
    rows = selectRows([...rows, ...page.rows]);
    cursor = page.next_cursor || undefined;
    if (cursor && seen.has(cursor))
      throw new ProgramSearchError(
        'Search pagination did not advance. Retry the search.'
      );
    if (cursor) seen.add(cursor);
    if (
      cursor &&
      rawFetched < limit &&
      (pages >= MAX_PROGRAM_IMPORT_PAGES ||
        emptyPages >= MAX_EMPTY_PROGRAM_IMPORT_PAGES)
    ) {
      throw new ProgramSearchError(
        'Import preparation reached its search limit. Narrow the programs or cohorts and retry.'
      );
    }
  } while (cursor && rawFetched < limit);
  return rows;
}
